import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import { Button } from '../Button/Button'
import { Spinner } from '../Spinner/Spinner'
import { HtmlPreview } from './HtmlPreview'
import { looksLikeHtmlDocument } from './htmlDocument'
import { extractOutputVarName, getFormContentInputNames, hasInvalidSelectOrFileInput, splitByOutputVar } from './formContent'
import { humanizeVariableName } from './variableLabel'
import { MarkdownViewer } from '../../views/AIAgent/Chat/MarkdownViewer'
import {
  CustomWorkflowFileReference,
  CustomWorkflowRunForm,
  CustomWorkflowRunFormField,
  CustomWorkflowRunInputValue,
  useUploadCustomWorkflowFileMutation,
} from '../../store/customWorkflow/customWorkflows'

export interface HumanInputFormViewProps {
  accountId: string
  workflowId: string
  form: CustomWorkflowRunForm
  submitting: boolean
  onSubmit: (actionId: string, inputs: Record<string, CustomWorkflowRunInputValue>) => void
}

const buildDefaults = (form: CustomWorkflowRunForm): Record<string, CustomWorkflowRunInputValue> => {
  const values: Record<string, CustomWorkflowRunInputValue> = {}
  for (const field of form.fields) {
    if (field.type === 'checkbox-list') {
      values[field.variable] = []
    } else if (field.type !== 'file' && field.type !== 'file-list') {
      values[field.variable] = field.default ?? ''
    }
  }
  return values
}

const stringValue = (value: CustomWorkflowRunInputValue | undefined): string => (typeof value === 'string' ? value : '')

const FileFieldControl: React.FC<{
  accountId: string
  workflowId: string
  multiple: boolean
  onChange: (value: CustomWorkflowFileReference | CustomWorkflowFileReference[] | undefined) => void
}> = ({ accountId, workflowId, multiple, onChange }) => {
  const [upload, { isLoading }] = useUploadCustomWorkflowFileMutation()
  const [names, setNames] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  const onFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) {
      setNames([])
      onChange(undefined)
      return
    }
    setError(null)
    try {
      const uploaded = await Promise.all(Array.from(fileList).map((file) => upload({ accountId, workflowId, file }).unwrap()))
      setNames(uploaded.map((item) => item.name))
      onChange(multiple ? uploaded.map((item) => item.fileReference) : uploaded[0].fileReference)
    } catch {
      setError('Upload failed.')
      setNames([])
      onChange(undefined)
    }
  }

  return (
    <>
      <Form.Control type='file' multiple={multiple} onChange={(e) => void onFiles((e.target as HTMLInputElement).files)} />
      {isLoading ? <Spinner size='sm' /> : null}
      {names.length > 0 ? <div className='zocks-text-gray-600 paragraph-small mt-1'>{names.join(', ')}</div> : null}
      {error ? <div className='zocks-text-danger paragraph-small mt-1'>{error}</div> : null}
    </>
  )
}

const stringListValue = (value: CustomWorkflowRunInputValue | undefined): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []

const CheckboxListControl: React.FC<{
  field: CustomWorkflowRunFormField
  value: string[]
  onChange: (value: string[]) => void
}> = ({ field, value, onChange }) => {
  const toggle = (option: string, checked: boolean) => {
    const selected = new Set(value)
    if (checked) {
      selected.add(option)
    } else {
      selected.delete(option)
    }
    onChange(field.options.filter((item) => selected.has(item)))
  }

  return (
    <>
      {field.options.map((option, index) => (
        <Form.Check
          key={option}
          type='checkbox'
          id={`human-input-${field.variable}-${index}`}
          label={option}
          checked={value.includes(option)}
          onChange={(e) => toggle(option, e.target.checked)}
        />
      ))}
    </>
  )
}

const FieldControl: React.FC<{
  field: CustomWorkflowRunFormField
  value: string
  onChange: (value: string) => void
}> = ({ field, value, onChange }) => {
  if (field.type === 'select') {
    return (
      <Form.Select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value=''>Select…</option>
        {field.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Form.Select>
    )
  }
  if (looksLikeHtmlDocument(value)) {
    return <HtmlPreview html={value} title={field.variable} onChange={onChange} />
  }
  return <Form.Control as='textarea' rows={3} value={value} onChange={(e) => onChange(e.target.value)} />
}

export const HumanInputFormView: React.FC<HumanInputFormViewProps> = ({ accountId, workflowId, form, submitting, onSubmit }) => {
  const [values, setValues] = useState<Record<string, CustomWorkflowRunInputValue>>(() => buildDefaults(form))

  const setValue = (variable: string, value: CustomWorkflowRunInputValue | undefined) =>
    setValues((prev) => {
      const next = { ...prev }
      if (value === undefined) {
        delete next[variable]
      } else {
        next[variable] = value
      }
      return next
    })

  const isHtmlContent = looksLikeHtmlDocument(form.formContent)
  const segments = isHtmlContent ? [] : splitByOutputVar(form.formContent)
  const referencedVariables = new Set(isHtmlContent ? [] : getFormContentInputNames(form.formContent))
  const fieldsByVariable = new Map(form.fields.map((field) => [field.variable, field]))
  const unreferencedFields = form.fields.filter((field) => !referencedVariables.has(field.variable))
  const invalid = hasInvalidSelectOrFileInput(form.fields, values)

  const renderControl = (field: CustomWorkflowRunFormField) =>
    field.type === 'checkbox-list' ? (
      <CheckboxListControl field={field} value={stringListValue(values[field.variable])} onChange={(value) => setValue(field.variable, value)} />
    ) : field.type === 'file' || field.type === 'file-list' ? (
      <FileFieldControl
        accountId={accountId}
        workflowId={workflowId}
        multiple={field.type === 'file-list'}
        onChange={(value) => setValue(field.variable, value)}
      />
    ) : (
      <FieldControl field={field} value={stringValue(values[field.variable])} onChange={(value) => setValue(field.variable, value)} />
    )

  return (
    <div>
      {isHtmlContent ? (
        <div className='mb-3'>
          <HtmlPreview html={form.formContent} />
        </div>
      ) : null}
      <Form>
        {segments.map((segment, index) => {
          const variable = extractOutputVarName(segment)
          if (variable === null) {
            return segment.trim().length > 0 ? <MarkdownViewer key={index} markdown={segment} /> : null
          }
          const field = fieldsByVariable.get(variable)
          if (!field) {
            return null
          }
          return (
            <Form.Group className='mb-3' key={index} controlId={`human-input-${field.variable}`}>
              {renderControl(field)}
            </Form.Group>
          )
        })}
        {unreferencedFields.map((field) => (
          <Form.Group className='mb-3' key={field.variable} controlId={`human-input-${field.variable}`}>
            <Form.Label>{humanizeVariableName(field.variable)}</Form.Label>
            {renderControl(field)}
          </Form.Group>
        ))}
      </Form>
      <div className='d-flex zocks-gap-2'>
        {form.actions.map((action) => (
          <Button
            key={action.id}
            variant={action.buttonStyle === 'primary' ? 'primary' : 'secondary'}
            disabled={submitting || invalid}
            loading={submitting}
            onClick={() => onSubmit(action.id, values)}
          >
            {action.title || action.id}
          </Button>
        ))}
      </div>
    </div>
  )
}

export default HumanInputFormView
